<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { onDestroy, tick } from "svelte"
  import { userDisplayName } from "@kontextwerk/control-web-frame/logic/userDisplay"
  import Table, {
    type TableFilterValues,
    type TableRowsUpdater,
    type TableSortState,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import {
    adaptCaseCommunicationFeed,
    fetchCaseCommunicationFeed,
    type AdaptedCaseCommunicationFeed,
    type CaseCommunicationContactOrderSummary,
    type CaseCommunicationFeedCapabilities,
    type CaseCommunicationFeedQuery,
    type CaseCommunicationFeedResult,
    type CaseCommunicationFeedStep,
    type CaseCommunicationTimelineItem,
  } from "../../../../../../../api/kehrblatt/case-communications"
  import type {
    ContactOrder,
    ContactOrderStep,
  } from "../../../../../../../api/kehrblatt/contact-orders"
  import { fetchOrganizationUsersCached } from "../../../../../../../api/kehrblatt/organizations"
  import { filterSelectableOrganizationUsers } from "../../../../../_shared/_logic/organization-users"
  import OpenWorkOrderAction from "./_actions/OpenWorkOrderAction.actions"
  import { createContactOrderStepActions } from "./_actions/contactOrderStepActions.svelte"
  import TestStepDialog from "./_modules/test-step-dialog"
  import ContactCell from "./_modules/contact-cell"
  import HintCell from "./_modules/hint-cell"
  import UnifiedStatusCell from "./_modules/unified-status-cell"
  import WorkCell from "./_modules/work-cell"
  import CommunicationPlanFilterBar from "./_modules/communication-plan-filter-bar"
  import type {
    CaseCommunicationTableProps,
    CommunicationPlanEmployeeOption,
    CommunicationPlanFilterValue,
    ContactOrderPlanRow,
    ContactOrderStepPatch,
    ContactOrderStepPatchMap,
  } from "../../_logic/communication-plan-table-contract"
  import { buildCommunicationPlanColumns } from "./_logic/columns"
  import {
    communicationPlanExternalFilters,
    communicationPlanFeedFilterKey,
    communicationPlanFeedFilters,
    communicationPlanFiltersActive,
    createDefaultCommunicationPlanFilters,
    normalizeCommunicationPlanFilterValue,
    type CommunicationPlanFeedFilters,
  } from "./_shared/_logic/filters"
  import { stepPatchMatchesLiveStep } from "./_logic/stepPatch"
  import { buildCommunicationPlanFeedRows } from "./_logic/feedTableModel"
  import {
    GLOBAL_COMMUNICATION_PLAN_DEFAULT_SORT,
    SCOPED_COMMUNICATION_PLAN_DEFAULT_SORT,
    communicationPlanFeedSort,
    communicationPlanFeedSortKey,
    type CommunicationPlanFeedSort,
  } from "./_logic/sort"
  import {
    communicationPlanUrlStateId,
    readCommunicationPlanUrlState,
    writeCommunicationPlanUrlState,
  } from "./_logic/urlState"
  import {
    EXECUTE_NOW_REFRESH_ATTEMPTS,
    EXECUTE_NOW_REFRESH_INTERVAL_MS,
    isExecuteNowRefreshPending,
  } from "./_logic/executeNowRefresh"

  const OPTIMISTIC_STEP_PATCH_TTL_MS = 15_000
  const stepActions = createContactOrderStepActions()
  const DEFAULT_PAGE_SIZE = 50
  const SCHEDULE_EVENT_REF_SERVICE = "kehrblatt_control"
  const SCHEDULE_EVENT_REF_RESOURCE = "schedule_event"
  const SCHEDULE_EVENT_REF_ROLE = "SCHEDULE_EVENT"
  const NO_CASE_COMMUNICATION_CAPABILITIES: CaseCommunicationFeedCapabilities = {
    can_open_work_order: false,
    can_open_automation: false,
    can_test_step: false,
    can_manage_contact_order: false,
    can_edit_step: false,
    can_execute_now: false,
    can_retry: false,
    can_skip_wait: false,
    can_cancel_step: false,
    can_manage_letter: false,
    available_channels: [],
  }

  type ExecuteNowRefreshRun = {
    timer: ReturnType<typeof setTimeout> | null
    resolveWait: (() => void) | null
    requestKey: string
  }

  type ActivePage = {
    offset: number
    limit: number
    cursor: string | null
  }

  type TargetedStepRefreshMerge = {
    result: CaseCommunicationFeedResult
    liveStep: CaseCommunicationFeedStep | null
  }

  const emptyFeedResult = (): CaseCommunicationFeedResult => ({
    items: [],
    contact_order_summaries: [],
    total: 0,
    next_cursor: null,
  })

  let {
    organizationId,
    caseId = null,
    scheduleEventId = null,
    contactOrderId = null,
    emptyText = "Keine Terminkommunikation vorhanden.",
    filterMode = "compact",
    onPlanningContextInvalidated = async () => {},
  }: CaseCommunicationTableProps = $props()

  let feedResult = $state<CaseCommunicationFeedResult>(emptyFeedResult())
  let feedLoading = $state(false)
  let feedError = $state<string | null>(null)
  let activePage: ActivePage = { offset: 0, limit: DEFAULT_PAGE_SIZE, cursor: null }
  let cursorByOffset = new Map<number, string | null>([[0, null]])
  let lastRequestKey = ""
  let latestRequestToken = 0
  let feedAbortController: AbortController | null = null
  let activeFeedFilters: CommunicationPlanFeedFilters = communicationPlanFeedFilters(null)
  let optimisticStepPatches: ContactOrderStepPatchMap = {}
  let updateTableRows: TableRowsUpdater | null = null
  let rowsSyncQueued = false
  const optimisticStepPatchTimers = new Map<string, ReturnType<typeof setTimeout>>()
  const executeNowRefreshRuns = new Map<string, ExecuteNowRefreshRun>()
  const currentOrganizationScope = () => !caseId && !scheduleEventId && !contactOrderId
  let activeFeedSort: CommunicationPlanFeedSort = communicationPlanFeedSort(
    null,
    currentOrganizationScope(),
  )
  const emptyFilterValue = createDefaultCommunicationPlanFilters()
  let filterValuesByOrganization = $state<Record<string, CommunicationPlanFilterValue>>({})
  const employeeOptionsByOrganization = new Map<
    string,
    Promise<CommunicationPlanEmployeeOption[]>
  >()

  const employeeOptionsForOrganization = (scopeOrganizationId: string) => {
    const cached = employeeOptionsByOrganization.get(scopeOrganizationId)

    if (cached) return cached

    const request = fetchOrganizationUsersCached(scopeOrganizationId, { limit: 500 })
      .then((response) =>
        filterSelectableOrganizationUsers(response.items, null)
          .map((user) => ({
            id: String(user.id),
            label: userDisplayName(user) || user.email || "Unbekannt",
          }))
          .sort((left, right) => left.label.localeCompare(right.label, "de")),
      )
      .catch((error) => {
        console.error("case_communication_employee_filter_load_failed", error)
        throw error
      })
    employeeOptionsByOrganization.set(scopeOrganizationId, request)

    return request
  }

  const invalidatePageCursorCache = () => {
    cursorByOffset = new Map<number, string | null>([[0, null]])
  }

  const adaptedFeed = $derived<AdaptedCaseCommunicationFeed>(adaptCaseCommunicationFeed(feedResult))
  const organizationScope = $derived(!caseId && !scheduleEventId && !contactOrderId)
  const filterValue = $derived<CommunicationPlanFilterValue>(
    organizationScope
      ? (filterValuesByOrganization[organizationId] ??
          readCommunicationPlanUrlState(organizationId))
      : emptyFilterValue,
  )
  const externalFilters = $derived<TableFilterValues | null>(
    organizationScope ? communicationPlanExternalFilters(filterValue) : null,
  )
  const hasActiveFilters = $derived(
    organizationScope && communicationPlanFiltersActive(filterValue),
  )
  const tableRefreshKey = $derived(
    `${organizationId}|${caseId ?? ""}|${scheduleEventId ?? ""}|${contactOrderId ?? ""}`,
  )
  const tableDefaultSort = $derived(
    organizationScope
      ? GLOBAL_COMMUNICATION_PLAN_DEFAULT_SORT
      : SCOPED_COMMUNICATION_PLAN_DEFAULT_SORT,
  )
  const tableUrlStateId = $derived(
    organizationScope ? communicationPlanUrlStateId(organizationId) : null,
  )
  const columns = $derived(buildCommunicationPlanColumns())

  function syncRowsInPlace(result = feedResult) {
    const rows = buildRows(result)
    updateTableRows?.(() => rows, { total: result.total })
  }

  function requestRowsSync() {
    if (rowsSyncQueued || !updateTableRows) return

    rowsSyncQueued = true
    void tick().then(() => {
      rowsSyncQueued = false

      if (!updateTableRows) return

      syncRowsInPlace()
    })
  }

  const clearOptimisticStepPatch = (
    stepId: string,
    { syncRows = true }: { syncRows?: boolean } = {},
  ) => {
    const timer = optimisticStepPatchTimers.get(stepId)

    if (timer) clearTimeout(timer)

    optimisticStepPatchTimers.delete(stepId)

    if (!optimisticStepPatches[stepId]) return

    const next = { ...optimisticStepPatches }
    delete next[stepId]
    optimisticStepPatches = next

    if (syncRows) requestRowsSync()
  }

  const applyOptimisticStepPatch = (stepId: string, patch: ContactOrderStepPatch) => {
    if (!stepId || !patch) return

    optimisticStepPatches = {
      ...optimisticStepPatches,
      [stepId]: { ...(optimisticStepPatches[stepId] ?? {}), ...patch },
    }
    const existingTimer = optimisticStepPatchTimers.get(stepId)

    if (existingTimer) clearTimeout(existingTimer)

    optimisticStepPatchTimers.set(
      stepId,
      setTimeout(() => clearOptimisticStepPatch(stepId), OPTIMISTIC_STEP_PATCH_TTL_MS),
    )
    requestRowsSync()
  }

  const cancelExecuteNowRefresh = (stepId: string) => {
    const run = executeNowRefreshRuns.get(stepId)

    if (!run) return

    executeNowRefreshRuns.delete(stepId)

    if (run.timer) clearTimeout(run.timer)

    run.timer = null
    const resolveWait = run.resolveWait
    run.resolveWait = null
    resolveWait?.()
  }

  const reconcileOptimisticStepPatches = (result: CaseCommunicationFeedResult) => {
    if (!Object.keys(optimisticStepPatches).length) return

    const contactOrders = adaptCaseCommunicationFeed(result).contactOrders
    for (const [stepId, patch] of Object.entries(optimisticStepPatches)) {
      const liveStep = contactOrders
        .flatMap((order) => order.steps ?? [])
        .find((step) => step.id === stepId)

      if (!liveStep || stepPatchMatchesLiveStep(patch, liveStep as Record<string, unknown>)) {
        clearOptimisticStepPatch(stepId, { syncRows: false })
      }
    }
  }

  const replaceVisibleFeed = (result: CaseCommunicationFeedResult) => {
    feedResult = result
    reconcileOptimisticStepPatches(result)
  }

  const clearTransientRowState = () => {
    for (const stepId of Array.from(executeNowRefreshRuns.keys())) cancelExecuteNowRefresh(stepId)
    for (const timer of optimisticStepPatchTimers.values()) clearTimeout(timer)
    optimisticStepPatchTimers.clear()
    optimisticStepPatches = {}
  }

  const resetTableStateForRequest = (requestKey: string) => {
    if (requestKey === lastRequestKey) return

    lastRequestKey = requestKey
    clearTransientRowState()
    activePage = { offset: 0, limit: DEFAULT_PAGE_SIZE, cursor: null }
    invalidatePageCursorCache()
    replaceVisibleFeed(emptyFeedResult())
    feedError = null
  }

  const requestKeyFor = (filters: CommunicationPlanFeedFilters, sort: CommunicationPlanFeedSort) =>
    `${tableRefreshKey}|${communicationPlanFeedFilterKey(filters)}|${communicationPlanFeedSortKey(sort)}`

  const stalePageResult = () => ({ rows: [], total: feedResult.total })

  const abortFeedRequest = () => {
    feedAbortController?.abort()
    feedAbortController = null
  }

  const isAbortError = (error: unknown): boolean =>
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: string }).name === "AbortError"

  const feedQuery = ({
    limit,
    cursor,
    contactStepId = null,
    filters = activeFeedFilters,
    sort = activeFeedSort,
  }: {
    limit: number
    cursor: string | null
    contactStepId?: string | null
    filters?: CommunicationPlanFeedFilters
    sort?: CommunicationPlanFeedSort
  }): CaseCommunicationFeedQuery => ({
    organizationId,
    caseId,
    contactOrderId,
    contactStepId,
    refService: scheduleEventId ? SCHEDULE_EVENT_REF_SERVICE : null,
    refResource: scheduleEventId ? SCHEDULE_EVENT_REF_RESOURCE : null,
    refId: scheduleEventId,
    refRole: scheduleEventId ? SCHEDULE_EVENT_REF_ROLE : null,
    projection: "table" as const,
    sortBy: sort.sortBy,
    sortDirection: sort.sortDirection,
    searchQuery: filters.searchQuery,
    appointmentDateFrom: filters.appointmentDateFrom,
    appointmentDateTo: filters.appointmentDateTo,
    employeeIds: filters.employeeIds,
    channels: filters.channels,
    statusGroups: filters.statusGroups,
    limit,
    cursor,
  })

  const resolveCursorForOffset = async ({
    offset,
    limit,
    filters,
    sort,
    requestKey,
    requestToken,
    signal,
  }: {
    offset: number
    limit: number
    filters: CommunicationPlanFeedFilters
    sort: CommunicationPlanFeedSort
    requestKey: string
    requestToken: number
    signal: AbortSignal
  }): Promise<string | null> => {
    if (offset <= 0) return null

    const cached = cursorByOffset.get(offset)

    if (cached !== undefined) return cached

    let traversed = 0
    let cursor: string | null = null
    while (traversed < offset) {
      const pageLimit = Math.min(limit, offset - traversed)
      const page = await fetchCaseCommunicationFeed(
        feedQuery({ limit: pageLimit, cursor, filters, sort }),
        {
          signal,
        },
      )

      if (requestToken !== latestRequestToken || requestKey !== lastRequestKey) return null

      const consumed = page.items.length

      if (!consumed || !page.next_cursor) return null

      traversed += consumed
      cursor = page.next_cursor
      cursorByOffset.set(traversed, cursor)
    }

    return cursor
  }

  const loadFeedPage = async ({
    limit,
    offset,
    inPlace = false,
    filters: rawFilters = externalFilters ?? {},
    sort: rawSort = tableDefaultSort,
  }: {
    limit: number
    offset: number
    inPlace?: boolean
    filters?: TableFilterValues
    sort?: TableSortState
  }) => {
    const pageLimit = limit > 0 ? limit : DEFAULT_PAGE_SIZE
    const requestedFilters = organizationScope
      ? communicationPlanFeedFilters(rawFilters)
      : communicationPlanFeedFilters(null)
    const requestedSort = communicationPlanFeedSort(rawSort, organizationScope)
    const requestedKey = requestKeyFor(requestedFilters, requestedSort)
    resetTableStateForRequest(requestedKey)
    const requestToken = ++latestRequestToken
    abortFeedRequest()
    const requestController = new AbortController()
    feedAbortController = requestController

    if (!inPlace) {
      feedLoading = true
      feedError = null
    }

    try {
      const cachedCursor = cursorByOffset.get(offset)
      const cursor =
        inPlace && activePage.offset === offset && cachedCursor !== undefined
          ? cachedCursor
          : await resolveCursorForOffset({
              offset,
              limit: pageLimit,
              filters: requestedFilters,
              sort: requestedSort,
              requestKey: requestedKey,
              requestToken,
              signal: requestController.signal,
            })

      if (requestToken !== latestRequestToken || requestedKey !== lastRequestKey) {
        return stalePageResult()
      }

      const result = await fetchCaseCommunicationFeed(
        feedQuery({ limit: pageLimit, cursor, filters: requestedFilters, sort: requestedSort }),
        { signal: requestController.signal },
      )

      if (requestToken !== latestRequestToken || requestedKey !== lastRequestKey) {
        return stalePageResult()
      }

      activePage = { offset, limit: pageLimit, cursor }
      activeFeedFilters = requestedFilters
      activeFeedSort = requestedSort
      cursorByOffset.set(offset, cursor)

      if (result.next_cursor) cursorByOffset.set(offset + result.items.length, result.next_cursor)

      replaceVisibleFeed(result)
      await tick()
      const rows = buildRows()

      if (inPlace) updateTableRows?.(() => rows, { total: result.total })

      return { rows, total: result.total }
    } catch (error) {
      if (requestController.signal.aborted || isAbortError(error)) {
        return stalePageResult()
      }

      if (requestToken !== latestRequestToken || requestedKey !== lastRequestKey) {
        return stalePageResult()
      }

      console.error("case_communication_feed_load_failed", error)

      if (inPlace) throw error

      feedError = "Terminkommunikation konnte nicht geladen werden."

      return { rows: [], total: 0 }
    } finally {
      if (feedAbortController === requestController) feedAbortController = null

      if (!inPlace && requestToken === latestRequestToken) feedLoading = false
    }
  }

  const refreshVisibleFeed = async () => {
    await loadFeedPage({
      ...activePage,
      inPlace: true,
      filters: {
        searchQuery: activeFeedFilters.searchQuery,
        appointmentDateFrom: activeFeedFilters.appointmentDateFrom,
        appointmentDateTo: activeFeedFilters.appointmentDateTo,
        employeeIds: activeFeedFilters.employeeIds,
        channels: activeFeedFilters.channels,
        statusGroups: activeFeedFilters.statusGroups,
      },
      sort: {
        columnId: activeFeedSort.sortBy === "work_order_number" ? "work" : "contact",
        key: activeFeedSort.sortBy,
        dir: activeFeedSort.sortDirection,
      },
    })
  }

  const refreshOutreachContext = async () => {
    await Promise.all([refreshVisibleFeed(), onPlanningContextInvalidated()])
  }

  const communicationStepId = (item: CaseCommunicationTimelineItem) =>
    item.communication.contact_step_id ??
    item.links.find((link) => link.role === "CONTACT_STEP" && link.ref_resource === "contact_step")
      ?.ref_id ??
    null

  const communicationTime = (item: CaseCommunicationTimelineItem) => {
    const communication = item.communication
    const value =
      communication.read_at ??
      communication.delivered_at ??
      communication.completed_at ??
      communication.sent_at ??
      communication.accepted_at ??
      communication.failed_at ??
      communication.suppressed_at ??
      communication.canceled_at ??
      communication.queued_at ??
      communication.created_at
    const timestamp = Date.parse(value ?? "")

    return Number.isFinite(timestamp) ? timestamp : 0
  }

  const communicationsByStepId = $derived.by(() => {
    const map = new Map<string, CaseCommunicationTimelineItem[]>()
    for (const item of adaptedFeed.caseCommunications) {
      const stepId = communicationStepId(item)

      if (!stepId) continue

      const items = map.get(stepId) ?? []
      items.push(item)
      map.set(stepId, items)
    }
    for (const items of map.values())
      items.sort((left, right) => communicationTime(right) - communicationTime(left))

    return map
  })

  const voiceLikeChannels = new Set(["PHONE", "VOICE", "VOICE_BOT", "WEB_BOT"])

  const buildRows = (result = feedResult): ContactOrderPlanRow[] => {
    const feed = adaptCaseCommunicationFeed(result)

    return buildCommunicationPlanFeedRows({
      entries: feed.tableEntries,
      workOrders: feed.workOrders,
      properties: feed.properties,
      scheduleEvents: feed.scheduleEvents,
      customerById: Object.fromEntries(feed.customers.map((customer) => [customer.id, customer])),
      contactPointById: Object.fromEntries(
        feed.contactPoints.map((contactPoint) => [contactPoint.id, contactPoint]),
      ),
      stepPatches: optimisticStepPatches,
    })
  }

  const patchFeedStep = (contactOrderId: string, updatedStep: ContactOrderStep) => {
    invalidatePageCursorCache()
    const items = feedResult.items.map((entry) =>
      entry.contact_order_id === contactOrderId && entry.step?.id === updatedStep.id
        ? { ...entry, step: { ...entry.step, ...updatedStep } }
        : entry,
    )
    replaceVisibleFeed({ ...feedResult, items })
    requestRowsSync()
  }

  const patchFeedOrder = (updatedOrder: ContactOrder) => {
    invalidatePageCursorCache()
    const steps = updatedOrder.steps ?? []
    const existingSummary = feedResult.contact_order_summaries.find(
      (candidate) => candidate.id === updatedOrder.id,
    )
    const summary: CaseCommunicationContactOrderSummary = {
      id: updatedOrder.id,
      organization_id: updatedOrder.organization_id,
      case_id: updatedOrder.case_id ?? existingSummary?.case_id ?? "",
      status: updatedOrder.status,
      closed_reason: updatedOrder.closed_reason ?? null,
      created_at: updatedOrder.created_at ?? null,
      updated_at: updatedOrder.updated_at ?? null,
    }
    const updateEntry = (entry: CaseCommunicationFeedResult["items"][number]) => {
      if (entry.contact_order_id !== updatedOrder.id) return entry

      const updatedStep = entry.step?.id
        ? steps.find((candidate) => candidate.id === entry.step?.id)
        : null

      return {
        ...entry,
        contact_order_summary: summary,
        step:
          updatedStep && entry.step
            ? {
                ...entry.step,
                ...updatedStep,
                contact_order_id: updatedOrder.id,
              }
            : entry.step,
      }
    }
    const summaries = [
      ...feedResult.contact_order_summaries.filter((candidate) => candidate.id !== updatedOrder.id),
      summary,
    ]
    replaceVisibleFeed({
      ...feedResult,
      items: feedResult.items.map(updateEntry),
      contact_order_summaries: summaries,
    })
    requestRowsSync()
  }

  const waitForNextExecuteNowRefreshAttempt = (stepId: string, run: ExecuteNowRefreshRun) =>
    new Promise<void>((resolve) => {
      if (executeNowRefreshRuns.get(stepId) !== run) {
        resolve()

        return
      }

      run.resolveWait = resolve
      run.timer = setTimeout(() => {
        run.timer = null
        run.resolveWait = null
        resolve()
      }, EXECUTE_NOW_REFRESH_INTERVAL_MS)
    })

  const isTargetedContactStepEntry = (
    entry: CaseCommunicationFeedResult["items"][number],
    stepId: string,
  ) => entry.kind === "contact_step" && entry.step?.id === stepId

  const fetchTargetedStepRefresh = async (
    stepId: string,
    run: ExecuteNowRefreshRun,
  ): Promise<CaseCommunicationFeedResult | null> => {
    let cursor: string | null = null
    let result = emptyFeedResult()
    do {
      if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey)
        return null

      result = await fetchCaseCommunicationFeed(
        feedQuery({
          limit: DEFAULT_PAGE_SIZE,
          cursor,
          contactStepId: stepId,
          filters: activeFeedFilters,
          sort: activeFeedSort,
        }),
      )

      if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey)
        return null

      if (result.items.some((entry) => isTargetedContactStepEntry(entry, stepId))) return result

      cursor = result.next_cursor ?? null
    } while (cursor)

    return result
  }

  const mergeTargetedStepRefresh = (
    stepId: string,
    result: CaseCommunicationFeedResult,
  ): TargetedStepRefreshMerge => {
    // A refreshed step may receive a new effective_at and therefore move
    // across cursor-page boundaries. Never reuse offset cursors derived
    // from the previous ordering.
    invalidatePageCursorCache()
    const refreshed =
      result.items.find((entry) => isTargetedContactStepEntry(entry, stepId)) ?? null
    const currentIndex = feedResult.items.findIndex((entry) =>
      isTargetedContactStepEntry(entry, stepId),
    )
    const items = [...feedResult.items]
    let total = feedResult.total

    if (refreshed && currentIndex >= 0) items.splice(currentIndex, 1, refreshed)
    else if (refreshed) {
      items.push(refreshed)
      total += 1
    } else if (currentIndex >= 0) {
      items.splice(currentIndex, 1)
      total = Math.max(0, total - 1)
    }

    const summaries = new Map(
      feedResult.contact_order_summaries.map((summary) => [summary.id, summary]),
    )
    for (const summary of result.contact_order_summaries ?? []) summaries.set(summary.id, summary)
    const mergedResult = {
      ...feedResult,
      items,
      total,
      contact_order_summaries: Array.from(summaries.values()),
    }
    replaceVisibleFeed(mergedResult)

    return {
      result: mergedResult,
      liveStep: refreshed?.step ?? null,
    }
  }

  const refreshExecuteNowUntilSettled = async (stepId: string, run: ExecuteNowRefreshRun) => {
    for (let attempt = 0; attempt < EXECUTE_NOW_REFRESH_ATTEMPTS; attempt += 1) {
      if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey) return

      if (attempt > 0) await waitForNextExecuteNowRefreshAttempt(stepId, run)

      if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey) return

      let mergedRefresh: TargetedStepRefreshMerge | null = null
      try {
        const result = await fetchTargetedStepRefresh(stepId, run)

        if (result === null) return

        if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey) return

        mergedRefresh = mergeTargetedStepRefresh(stepId, result)
      } catch (error) {
        console.error("contact_order_step_execute_refresh_failed", error)
      }

      if (executeNowRefreshRuns.get(stepId) !== run || run.requestKey !== lastRequestKey) return

      if (!mergedRefresh) continue

      if (!isExecuteNowRefreshPending(mergedRefresh.liveStep)) {
        clearOptimisticStepPatch(stepId, { syncRows: false })
        syncRowsInPlace(mergedRefresh.result)
        cancelExecuteNowRefresh(stepId)

        return
      }

      syncRowsInPlace(mergedRefresh.result)
    }

    if (executeNowRefreshRuns.get(stepId) === run) executeNowRefreshRuns.delete(stepId)
  }

  const handleExecuteNowStarted = (stepId: string) => {
    if (!stepId) return

    cancelExecuteNowRefresh(stepId)
    const run: ExecuteNowRefreshRun = {
      timer: null,
      resolveWait: null,
      requestKey: lastRequestKey || requestKeyFor(activeFeedFilters, activeFeedSort),
    }
    executeNowRefreshRuns.set(stepId, run)
    void refreshExecuteNowUntilSettled(stepId, run)
  }

  const prepareForFilterChange = () => {
    abortFeedRequest()
    latestRequestToken += 1
    lastRequestKey = ""
    activePage = { offset: 0, limit: DEFAULT_PAGE_SIZE, cursor: null }
    invalidatePageCursorCache()
    clearTransientRowState()
    feedError = null
  }

  const handleFilterChange = (nextValue: CommunicationPlanFilterValue) => {
    if (!organizationScope) return

    const normalized = normalizeCommunicationPlanFilterValue(nextValue)
    const currentKey = communicationPlanFeedFilterKey(
      communicationPlanFeedFilters(communicationPlanExternalFilters(filterValue)),
    )
    const nextKey = communicationPlanFeedFilterKey(
      communicationPlanFeedFilters(communicationPlanExternalFilters(normalized)),
    )

    if (currentKey === nextKey) return

    prepareForFilterChange()
    filterValuesByOrganization = {
      ...filterValuesByOrganization,
      [organizationId]: normalized,
    }
    writeCommunicationPlanUrlState(organizationId, normalized)
  }

  const capabilitiesForRow = (row: ContactOrderPlanRow): CaseCommunicationFeedCapabilities =>
    adaptedFeed.capabilitiesByEntryKey[row.id] ?? NO_CASE_COMMUNICATION_CAPABILITIES

  const automationContextForRow = (row: ContactOrderPlanRow) =>
    adaptedFeed.extensionByEntryKey[row.id]?.automation_context ?? null

  const rowHasStepAction = (capabilities: CaseCommunicationFeedCapabilities) =>
    Boolean(
      capabilities.can_manage_contact_order ||
      capabilities.can_edit_step ||
      capabilities.can_execute_now ||
      capabilities.can_retry ||
      capabilities.can_skip_wait ||
      capabilities.can_cancel_step ||
      capabilities.can_manage_letter ||
      capabilities.can_test_step ||
      capabilities.can_open_automation,
    )

  onDestroy(() => {
    abortFeedRequest()
    latestRequestToken += 1
    updateTableRows = null
    for (const timer of optimisticStepPatchTimers.values()) clearTimeout(timer)
    optimisticStepPatchTimers.clear()
    for (const stepId of Array.from(executeNowRefreshRuns.keys())) cancelExecuteNowRefresh(stepId)
  })
</script>

{#if organizationScope}
  {#await employeeOptionsForOrganization(organizationId)}
    <CommunicationPlanFilterBar
      value={filterValue}
      {filterMode}
      employees={[]}
      employeeLoading={true}
      onChange={handleFilterChange}
    />
  {:then employeeOptions}
    <CommunicationPlanFilterBar
      value={filterValue}
      {filterMode}
      employees={employeeOptions}
      onChange={handleFilterChange}
    />
  {:catch}
    <CommunicationPlanFilterBar
      value={filterValue}
      {filterMode}
      employees={[]}
      employeeError="Mitarbeiter konnten nicht geladen werden."
      onChange={handleFilterChange}
    />
  {/await}
{/if}

<Table
  {columns}
  getRows={({ filters, limit, offset, sort }) => loadFeedPage({ filters, limit, offset, sort })}
  {externalFilters}
  defaultSort={tableDefaultSort}
  urlStateId={tableUrlStateId}
  refreshKey={tableRefreshKey}
  setRowsUpdater={(updater) => {
    updateTableRows = updater
  }}
  theme="light"
  mode="table-scroll"
  actionsHeaderLabel="Aktionen"
  showActions={true}
  rowActions={{
    resolve: (row) => {
      const capabilities = capabilitiesForRow(row)
      const actions =
        row.workOrderId && capabilities.can_open_work_order
          ? OpenWorkOrderAction({
              workOrderId: row.workOrderId,
              organizationId,
              organizationSlug: null,
              organizationPathSegment: organizationId,
            })
          : []

      if (row.stepActionContext && rowHasStepAction(capabilities)) {
        actions.push(
          ...stepActions.build({
            row,
            order: row.stepActionContext.order,
            organizationId,
            organizationSlug: null,
            contactPoints: adaptedFeed.contactPoints,
            customers: adaptedFeed.customers,
            availableChannels: adaptedFeed.allowedChannels,
            communicationsByStepId,
            automationContext: automationContextForRow(row),
            voiceLikeChannels,
            capabilities,
            onApplyStepPatch: applyOptimisticStepPatch,
            onContactOrderUpdated: patchFeedOrder,
            onContactOrderStepUpdated: patchFeedStep,
            onReloadContactOrders: refreshVisibleFeed,
            onReloadOutreach: refreshOutreachContext,
            onExecuteNowStarted: handleExecuteNowStarted,
          }),
        )
      }

      return buildTableRowActionSet(actions, {
        primaryId: "action-0",
        inlineIds: [
          "LetterStepActions.action-0",
          "RetryStepAction.action-0",
          "EditStepAction.action-0",
        ],
      })
    },
  }}
>
  {#snippet tableCell({ row, column })}
    {#if column.id === "work"}
      <WorkCell {row} />
    {:else if column.id === "contact"}
      <ContactCell {row} />
    {:else if column.id === "status"}
      <UnifiedStatusCell {row} />
    {:else if column.id === "hint"}
      <HintCell {row} />
    {:else}
      {row[column.id] ?? ""}
    {/if}
  {/snippet}
  {#snippet empty()}
    <div class="empty-state">
      {#if feedLoading}
        <p>Terminkommunikation wird geladen.</p>
      {:else if feedError}
        <p>{feedError}</p>
      {:else if hasActiveFilters}
        <p>Keine Terminkommunikation entspricht den Filtern.</p>
      {:else}
        <p>{emptyText}</p>
      {/if}
    </div>
  {/snippet}
</Table>

{#if stepActions.testContext}
  <TestStepDialog {...stepActions.testContext} onClose={stepActions.closeTest} />
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .empty-state {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0.6rem 0;

    p {
      margin: 0;
      color: color-mix(in srgb, var(--text-invers-100) 76%, transparent);
    }
  }
</style>
